import { useEffect } from 'react'
import { useLocale } from './useLocale'

/**
 * Hook to localize Chatlio widget based on the current language
 */
export const useChatlioLocalization = () => {
  // Initialize Chatlio localization
  const { i18n, t } = useLocale()

  useEffect(() => {
    // Configure Chatlio with the correct language based on current locale
    const configureChatlio = () => {
      // Messages when widget is online
      const currentLanguage = i18n.language
      // Russian uses different keys due to historical reasons
      const onlineTitle =
        currentLanguage === 'ru'
          ? t('chatlio_online_title', 'How can we help you?')
          : t('chatlio_title', 'How can we help you?')
      const onlineMessagePlaceholder = t(
        'chatlio_message_placeholder',
        'Type message here...'
      )
      const autoResponseMessage = t(
        'chatlio_auto_response',
        'Hi there!\n\nWe’re here to assist you with any questions or issues you may have. You can expect a response from our team within 5 min.\n\nYou’re welcome to reach out with any questions. Some common topics include:\n\n1. Help booking or canceling a session\n2. Changing coaches or seeing other coach profiles\n3. Managing Hupo Perspectives 360\n\nIf your question doesn’t fall into one of these, no problem, just drop us a message and our team will point you in the right direction.'
      )

      // Messages when widget is offline
      const offlineTitle = t('chatlio_offline_title', 'Contact Us')
      const offlineGreeting = t(
        'chatlio_offline_greeting',
        'Sorry we are away, but we would love to hear from you and chat soon!'
      )
      const offlineEmailPlaceholder = t(
        'chatlio_offline_email_placeholder',
        'Email'
      )
      const offlineMessagePlaceholder = t(
        'chatlio_offline_message_placeholder',
        'Your message here'
      )
      const offlineNamePlaceholder = t(
        'chatlio_offline_name_placeholder',
        'Name (optional but helpful)'
      )
      const offlineSendButton = t('chatlio_offline_send_button', 'Send')
      const offlineThankYouMessage = t(
        'chatlio_offline_thank_you',
        'Thanks for your message. We will be in touch soon!'
      )

      // Pre-chat form messages
      const requireInfoGreeting = t(
        'chatlio_require_info_greeting',
        'Enter your name and email to start chatting!'
      )
      const requireInfoSubmitBtn = t('chatlio_require_info_submit', 'Start')

      // Conversation end messages
      const conversationEndLabel = t(
        'chatlio_conversation_end_label',
        'Your chat session has ended. Thanks for chatting!'
      )
      const conversationRatingLabel = t(
        'chatlio_conversation_rating_label',
        'How would you rate this chat?'
      )
      const conversationRatingThankYou = t(
        'chatlio_conversation_rating_thank_you',
        'Thanks for rating your chat session!'
      )
      const conversationRatingPlaceholder = t(
        'chatlio_conversation_rating_placeholder',
        'How can we improve?'
      )
      const conversationEndTranscriptPlaceholder = t(
        'chatlio_end_transcript_placeholder',
        'Email to send transcript of chat'
      )
      const conversationEndConfirmationQuestion = t(
        'chatlio_end_confirmation_question',
        'Are you sure you want to end this chat?'
      )
      const conversationTranscriptSentThankYou = t(
        'chatlio_transcript_sent_thank_you',
        'Thanks! You will receive your transcript shortly.'
      )
      const conversationEndMenuLinkLabel = t(
        'chatlio_end_menu_link_label',
        'End Chat'
      )

      // Button labels
      const defaultSendBtnLabel = t('chatlio_send_btn_label', 'Send')
      const defaultOkBtnLabel = t('chatlio_ok_btn_label', 'Ok')
      const defaultCancelBtnLabel = t('chatlio_cancel_btn_label', 'Cancel')
      const defaultYesBtnLabel = t('chatlio_yes_btn_label', 'Yes')
      const defaultNoBtnLabel = t('chatlio_no_btn_label', 'No')

      // Screenshot request messages
      const requestScreenshotText = t(
        'chatlio_request_screenshot_text',
        'Operator would like to take a screenshot of your browser. Confirm below.'
      )
      const requestScreenshotAllowLabel = t(
        'chatlio_request_screenshot_allow',
        'Take screenshot'
      )
      const requestScreenshotDeclineLabel = t(
        'chatlio_request_screenshot_decline',
        'Decline'
      )

      // No answer messages
      const noAnswerWithEmail = t(
        'chatlio_no_answer_with_email',
        'Oops! Sorry no one has responded yet. We have your email on file if you need to leave or you can continue to wait.'
      )
      const noAnswerWithoutEmail = t(
        'chatlio_no_answer_without_email',
        'Oops! Sorry no one has responded yet. We have your email on file if you need to leave or you can continue to wait.'
      )

      if (window._chatlio && window._chatlio.configure) {
        window._chatlio.configure({
          // Widget titles
          onlineTitle,
          offlineTitle,

          // Online chat messages
          onlineMessagePlaceholder,
          autoResponseMessage,

          // Offline form messages
          offlineGreeting,
          offlineEmailPlaceholder,
          offlineMessagePlaceholder,
          offlineNamePlaceholder,
          offlineSendButton,
          offlineThankYouMessage,

          // Pre-chat form messages
          requireInfoGreeting,
          requireInfoSubmitBtn,

          // Conversation end messages
          conversationEndLabel,
          conversationRatingLabel,
          conversationRatingThankYou,
          conversationRatingPlaceholder,
          conversationEndTranscriptPlaceholder,
          conversationEndConfirmationQuestion,
          conversationTranscriptSentThankYou,
          conversationEndMenuLinkLabel,

          // Button labels
          defaultSendBtnLabel,
          defaultOkBtnLabel,
          defaultCancelBtnLabel,
          defaultYesBtnLabel,
          defaultNoBtnLabel,

          // Screenshot request messages
          requestScreenshotText,
          requestScreenshotAllowLabel,
          requestScreenshotDeclineLabel,

          // No answer messages
          noAnswerWithEmail,
          noAnswerWithoutEmail,
        })
      }
    }

    // Listen for Chatlio to be ready before configuring
    const handleChatlioReady = () => {
      configureChatlio()
    }

    // Set up event listener
    document.addEventListener('chatlio.ready', handleChatlioReady)

    // Also try to configure immediately in case Chatlio is already loaded
    if (window._chatlio) {
      configureChatlio()
    }

    // Clean up
    return () => {
      document.removeEventListener('chatlio.ready', handleChatlioReady)
    }
  }, [i18n.language]) // Re-run when language changes
}
